import { useTranslate, useMany, type CrudFilter } from '@refinedev/core';
import { List, EditButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Form, Row, Col, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type {
  TestimonialOut,
  PublicationListOut,
} from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { SelectFilter } from '@/components/common/SelectFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { PublicationSelectFilter } from '@/components/user_catalog/publications/PublicationSelectFilter';
import { PromotedTag } from '@/components/feedback/tags';
import { isFieldFiltered } from '@/utils/filters';
import { formatDateTime } from '@/utils/format';
import { getSortOrder } from '@/utils/sorters';

export const TestimonialList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<TestimonialOut>({
      pagination: { pageSize: 20 },
      resource: 'feedback.testimonials',
      onSearch,
      sorters: {
        initial: [{ field: 'created_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  const publicationIds = [
    ...new Set(
      (tableProps.dataSource ?? []).map((r) => r.publication_id).filter(Boolean)
    ),
  ];

  const { result: publicationsResult } = useMany<PublicationListOut>({
    resource: 'user_catalog.publications',
    ids: publicationIds,
    queryOptions: { enabled: publicationIds.length > 0 },
  });

  const publicationMap = new Map<number, PublicationListOut>(
    (publicationsResult?.data ?? []).map((p) => [p.id, p])
  );

  return (
    <List canCreate>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('feedback.testimonials.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table {...rowNav('feedback.testimonials', tableProps)} rowKey="id">
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="publication_id"
          title={t('feedback.testimonials.fields.publication_id')}
          render={(value: number) => (
            <PublicationInfoPopover
              publication={publicationMap.get(value)}
              publicationId={value}
            />
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'publication_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublicationSelectFilter
              field="publication_id"
              placeholder={t('feedback.testimonials.filter.publication_id')}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="author_name"
          title={t('feedback.testimonials.fields.author_name')}
        />

        <Table.Column
          dataIndex="comment"
          title={t('feedback.testimonials.fields.comment')}
          render={(value: string) =>
            value?.length > 80 ? `${value.slice(0, 80)}…` : value
          }
        />

        <Table.Column
          dataIndex="is_promoted"
          title={t('feedback.testimonials.fields.is_promoted')}
          sorter
          sortOrder={getSortOrder(sorters, 'is_promoted')}
          render={(value: boolean) => <PromotedTag value={value} />}
          filtered={isFieldFiltered(filters as CrudFilter[], 'is_promoted')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter<boolean>
              field="is_promoted"
              minWidth={160}
              placeholder={t('feedback.testimonials.fields.is_promoted')}
              options={[
                {
                  label: t('feedback.testimonials.filter.promoted'),
                  value: true,
                },
                {
                  label: t('feedback.testimonials.filter.not_promoted'),
                  value: false,
                },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="created_at"
          title={t('base.fields.created_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'created_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'created_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="created_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) => formatDateTime(value)}
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: TestimonialOut) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
